import { APP_INITIALIZER, ErrorHandler, isDevMode, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';

import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ErrorHandlerService } from './@core/services/error-handler.service';
import { ErrorHandlerInterceptor } from './error-handler.interceptor';
import { ConfigService } from './@core/services/config.service';
import { appReducer } from './@store/app.reducer';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { SpinnerModule } from 'primeng/spinner';
import { LayoutModule } from './layout';
import { getEnTranslation, getHeTranslation } from '../assets/i18n';
import { environment } from '../environments/environment';
import { AppEffects } from './@store/app.effects';
import { FinancialOfferService } from './financial-offers/http/financial-offer.service';
import { DocSpringService } from './@core/services/doc-spring.service';
import { LoaderInterceptor } from './@core/interceptors/async-loader.interceptor';
import { AnalyticsModule } from './@core/modules/analytics';
import { HostedFieldsService } from './@core/services/hosted-fields.service';
import { DialogService } from 'primeng/dynamicdialog';
import { CustomerSupportModule } from './@core/modules/customer-support';
import { SellerModule } from './seller';
import { ApiDomainInterceptor } from './@core/interceptors/api-domain.interceptor';

// Factory function required during AOT compilation
export function httpTranslateLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

const initializeConfig = (config: ConfigService) => {
  return () => config.load();
};
const initializeTranslation = (translate: TranslateService) => {
  return () => {
    translate.addLangs(environment.languages);
    translate.setTranslation('en', getEnTranslation());
    translate.setTranslation('he', getHeTranslation());
    translate.setDefaultLang(environment.defaultLanguage);
    translate.use(environment.defaultLanguage);
  };
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoaderFactory,
        deps: [HttpClient],
      },
    }),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    StoreModule.forRoot(
      { app: appReducer },
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictActionSerializability: true,
          strictStateSerializability: true,
        },
      },
    ),
    EffectsModule.forRoot([AppEffects]),
    LayoutModule,
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
    AnalyticsModule,
    CustomerSupportModule,
    SellerModule,
    DropdownModule,
    FormsModule,
    SpinnerModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeConfig,
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTranslation,
      deps: [TranslateService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiDomainInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    ConfigService,
    FinancialOfferService,
    DocSpringService,
    HostedFieldsService,
    DialogService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
